import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dropzone/styles.css";
import "@gfazioli/mantine-audio/styles.css";
import "@/styles/fonts.css";
import "@/styles/global.css";

import { useState } from "react";

import { localStorageColorSchemeManager, MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";

import APIHelper from "@/Api/APIHelper";
import { AudioPlayerProvider } from "@/audio/AudioPlayerProvider";
import { AuthProvider } from "@/Components/Auth/AuthProvider";
import { initAnalytics } from "@/config/analytics";
import { getI18nLocale } from "@/config/i18n";
import { ApiHelperContext } from "@/contexts";
import Router from "@/Router";
import { hookyCssVariablesResolver, hookyTheme } from "@/theme";

initAnalytics();

// The localStorage "theme" key ("light" | "dark") is shared with the legacy
// app; absence falls back to the OS scheme.
const colorSchemeManager = localStorageColorSchemeManager({ key: "theme" });

function createQueryClient() {
	return new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnWindowFocus: false,
				staleTime: 60000,
				retry: (failureCount, error) => {
					const status = (error as { response?: { status?: number } })
						?.response?.status;
					if (
						status &&
						[500, 502, 503, 504].includes(status) &&
						failureCount < 3
					) {
						return true;
					}
					return false;
				},
			},
		},
	});
}

export default function App() {
	const [queryClient] = useState(createQueryClient);
	const [apiHelper] = useState(() => new APIHelper());
	const { i18n } = useTranslation();

	return (
		<QueryClientProvider client={queryClient}>
			<MantineProvider
				theme={hookyTheme}
				cssVariablesResolver={hookyCssVariablesResolver}
				defaultColorScheme="auto"
				colorSchemeManager={colorSchemeManager}
			>
				<DatesProvider
					settings={{ locale: getI18nLocale(i18n.resolvedLanguage) }}
				>
					<ModalsProvider>
						<Notifications position="top-right" autoClose={3000} />
						<ApiHelperContext.Provider value={apiHelper}>
							<BrowserRouter>
								<AudioPlayerProvider>
									<AuthProvider>
										<Router />
									</AuthProvider>
								</AudioPlayerProvider>
							</BrowserRouter>
						</ApiHelperContext.Provider>
					</ModalsProvider>
				</DatesProvider>
			</MantineProvider>
		</QueryClientProvider>
	);
}
